import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import type { ImageBlockAttsInterface } from '~/types/AppData/Blocks/ImageBlockInterface';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import { cva } from 'class-variance-authority';
import Image from '~/components/Shared/Image/Image';
import {
  getCSSColorText,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { AppDataContext } from '~/contexts/AppDataContext';
import { ConditionalWrapper } from '~/components/Shared/Utility/ConditionalWrapper/ConditionalWrapper';
import { useIsPremiumTemplate } from '~/utilities/useIsPremiumTemplate';

const cssClasses = cva('m-3', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
    isPremium: {
      true: 'my-3 mx-5 lg:mx-0',
      false: 'm-3',
    },
  },
});

export default component$(
  ({
    alt,
    aspectRatio,
    path,
    caption,
    credit,
    cropping,
    fileExtension,
    linkURL,
  }: ImageBlockAttsInterface): JSX.Element => {
    const {
      themeComponents: { image },
    } = useContext(AppDataContext);
    const hasLinkUrl = !!(linkURL && linkURL !== '');
    // default article image sizes
    const widths = { small: 360, medium: 720, large: 720 };

    const isPremium = useIsPremiumTemplate();
    const isVertical = aspectRatio === '2/3' || aspectRatio === '3/4';
    const divWrapperClass = `my-[30px] -mx-5 ${isVertical ? 'lg:mx-0' : 'lg:-mx-[125px]'}`;

    return (
      <ConditionalWrapper
        component='div'
        condition={isPremium}
        props={{ class: divWrapperClass }}
      >
        <ConditionalWrapper
          component='a'
          condition={hasLinkUrl}
          props={{ href: linkURL, target: '_blank' }}
        >
          <Image
            alt={alt}
            aspectRatio={aspectRatio}
            path={path}
            caption={caption}
            credit={credit}
            cropping={cropping}
            fileExtension={fileExtension}
            loading='lazy'
            imageWidths={widths}
            captionClass={cssClasses({
              isPremium,
              color: image.textColor.value,
              ...getFontSizeObject(image, 'fontSize'),
            })}
          />
        </ConditionalWrapper>
      </ConditionalWrapper>
    );
  }
);
